import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';

// A friendly date (and optional time) picker: readable display, a month grid,
// and fast month/year dropdowns so decades-back birth dates take two clicks,
// not two hundred. Values stay plain strings — 'YYYY-MM-DD', or
// 'YYYY-MM-DDTHH:MM' with time — built from LOCAL date parts (never
// toISOString, which would shift days across timezones).

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const pad = (n: number) => String(n).padStart(2, '0');
const fmtDate = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;

function splitValue(value: string): { date: string; time: string } {
  const [date = '', time = ''] = value.split('T');
  return { date, time: time.slice(0, 5) };
}

function parseDate(date: string): { y: number; m: number; d: number } | null {
  const m = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]) - 1, d: Number(m[3]) };
}

function displayLabel(value: string, withTime: boolean): string {
  const { date, time } = splitValue(value);
  const p = parseDate(date);
  if (!p) return '';
  const base = `${MONTHS[p.m].slice(0, 3)} ${p.d}, ${p.y}`;
  return withTime && time ? `${base} · ${time}` : base;
}

export interface DateFieldProps {
  /** '' | 'YYYY-MM-DD' (date) | 'YYYY-MM-DDTHH:MM' (withTime). */
  value: string;
  onChange: (value: string) => void;
  withTime?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** Years offered in the year dropdown (inclusive). */
  fromYear?: number;
  toYear?: number;
}

export function DateField({
  value,
  onChange,
  withTime = false,
  disabled = false,
  placeholder = 'Select date',
  fromYear = 1900,
  toYear = new Date().getFullYear() + 1,
}: DateFieldProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const { date, time } = splitValue(value);
  const selected = parseDate(date);
  const today = new Date();
  const [view, setView] = useState<{ y: number; m: number }>(() => ({
    y: selected?.y ?? today.getFullYear(),
    m: selected?.m ?? today.getMonth(),
  }));

  // Reopen on the selected month even after external value changes.
  useEffect(() => {
    if (open && selected) setView({ y: selected.y, m: selected.m });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (root.current?.contains(t) || panel.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // The panel is portaled with FIXED coordinates; any scroll would desync
    // it from the trigger, so scrolling simply closes it (capture phase sees
    // scrolls inside the form's scroll container too).
    const onScroll = (e: Event) => {
      if (panel.current && e.target instanceof Node && panel.current.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const grid = useMemo(() => {
    const first = new Date(view.y, view.m, 1).getDay();
    const days = new Date(view.y, view.m + 1, 0).getDate();
    const cells: (number | null)[] = Array.from({ length: first }, () => null);
    for (let d = 1; d <= days; d++) cells.push(d);
    return cells;
  }, [view]);

  const years = useMemo(() => {
    const out: number[] = [];
    for (let y = toYear; y >= fromYear; y--) out.push(y);
    return out;
  }, [fromYear, toYear]);

  const setDate = (y: number, m: number, d: number, close: boolean) => {
    const dv = fmtDate(y, m, d);
    onChange(withTime ? `${dv}T${time || '00:00'}` : dv);
    if (close) setOpen(false);
  };

  const setTime = (t: string) => {
    if (!t) return;
    const dv = date || fmtDate(today.getFullYear(), today.getMonth(), today.getDate());
    onChange(`${dv}T${t}`);
  };

  const shiftMonth = (delta: number) => {
    setView((v) => {
      const m = v.m + delta;
      if (m < 0) return { y: v.y - 1, m: 11 };
      if (m > 11) return { y: v.y + 1, m: 0 };
      return { ...v, m };
    });
  };

  const label = displayLabel(value, withTime);

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (open) {
            setOpen(false);
            return;
          }
          const r = root.current?.getBoundingClientRect();
          if (r) {
            const W = 256; // w-64
            const H = withTime ? 372 : 336; // measured panel heights
            let top = r.bottom + 4;
            if (top + H > window.innerHeight - 8) top = Math.max(8, r.top - H - 4);
            // max() LAST so the 8px floor wins even on tiny viewports.
            const left = Math.max(8, Math.min(r.left, window.innerWidth - W - 8));
            setPos({ top, left });
          }
          setOpen(true);
        }}
        className={cn(
          'flex h-9 w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 py-1 text-left text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
          !label && 'text-muted-foreground',
        )}
      >
        <CalendarIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="flex-1 truncate">{label || placeholder}</span>
        {label && !disabled && (
          <span
            role="button"
            aria-label="Clear date"
            className="rounded p-0.5 hover:bg-accent"
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
              setOpen(false);
            }}
          >
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </span>
        )}
      </button>

      {open &&
        pos &&
        createPortal(
        <div
          ref={panel}
          style={{ top: pos.top, left: pos.left }}
          className="fixed z-50 w-64 rounded-md border bg-background p-2.5 shadow-lg"
        >
          <div className="mb-2 flex items-center gap-1">
            <button
              type="button"
              onClick={() => shiftMonth(-1)}
              className="rounded p-1 hover:bg-accent"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <select
              value={view.m}
              onChange={(e) => setView((v) => ({ ...v, m: Number(e.target.value) }))}
              className="h-7 flex-1 rounded-md border border-input bg-background px-1 text-xs focus:outline-none"
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i}>
                  {m}
                </option>
              ))}
            </select>
            <select
              value={view.y}
              onChange={(e) => setView((v) => ({ ...v, y: Number(e.target.value) }))}
              className="h-7 w-20 rounded-md border border-input bg-background px-1 text-xs focus:outline-none"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => shiftMonth(1)}
              className="rounded p-1 hover:bg-accent"
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 text-center">
            {WEEKDAYS.map((d) => (
              <div key={d} className="py-1 text-[10px] font-medium text-muted-foreground">
                {d}
              </div>
            ))}
            {grid.map((d, i) =>
              d === null ? (
                <div key={`b${i}`} />
              ) : (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDate(view.y, view.m, d, !withTime)}
                  className={cn(
                    'rounded py-1 text-xs tabular-nums hover:bg-accent',
                    selected &&
                      selected.y === view.y &&
                      selected.m === view.m &&
                      selected.d === d &&
                      'bg-primary text-primary-foreground hover:bg-primary',
                    view.y === today.getFullYear() &&
                      view.m === today.getMonth() &&
                      d === today.getDate() &&
                      'font-bold underline underline-offset-2',
                  )}
                >
                  {d}
                </button>
              ),
            )}
          </div>

          <div className="mt-2 flex items-center gap-2 border-t pt-2">
            {withTime && (
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="h-7 rounded-md border border-input bg-background px-1.5 text-xs focus:outline-none"
              />
            )}
            <button
              type="button"
              className="ml-auto rounded px-2 py-1 text-xs text-muted-foreground hover:bg-accent"
              onClick={() =>
                setDate(today.getFullYear(), today.getMonth(), today.getDate(), !withTime)
              }
            >
              Today
            </button>
            {withTime && (
              <button
                type="button"
                className="rounded bg-primary px-2 py-1 text-xs text-primary-foreground hover:bg-primary/90"
                onClick={() => setOpen(false)}
              >
                Done
              </button>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
